import { log } from 'log';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { clone, mergeDeepWith, pick } from 'ramda';
import { useGetPlayerTags } from 'js/hooks/socket/hooks';
import { useUserSelector, useDeviceSelector } from 'js/store/hooks';
import { useGetFavoriteGames, useGetPggGames } from 'js/hooks/games';
import { IGamesFeed, IGame, CATEGORY_TYPES, CATEGORY_VIEWS } from 'js/hooks/games/types';

import * as helpers from './helpers';
import * as Types from '../../types';
import { isGameAvailable } from '../../helpers';

export const useParseGamesFeed = () => {
    const getPggGames = useGetPggGames();
    const getPlayerTags = useGetPlayerTags();
    const getFavoriteGames = useGetFavoriteGames();
    const { param2: categoryNameURLParam } = useParams<{ param2: string }>();
    const { formFactor, os, browserName } = useDeviceSelector(pick(['formFactor', 'os', 'browserName']));
    const { isLoggedIn, lang, urlTemplates, country, userDetails } = useUserSelector(pick(['isLoggedIn', 'lang', 'urlTemplates', 'country', 'userDetails']));
    const { isInternalPlayer } = userDetails;

    return useCallback(async ({ gamesFeed, allGames, isViewMoreMode }: {
        gamesFeed: IGamesFeed;
        isViewMoreMode?: boolean;
        allGames: { [key: string]: IGame };
    }): Promise<Types.TGamesFeedParsed> => {
        let pggCategoryResponses: Awaited<ReturnType<typeof getPggGames>>[] = [];
        const liveGamesAliases = new Set<string>();
        let recentlyPlayedGames: string[] = [];

        const { filteredCategories, categoriesInfo } = await helpers.filterCategories({
            os,
            lang,
            country,
            isLoggedIn,
            formFactor,
            browserName,
            urlTemplates,
            getPlayerTags,
            categoryNameURLParam,
            gamesFeedCategories: gamesFeed.gamesFeedCategories
        });

        // getting recently played games
        if (categoriesInfo.isRecentlyPlayedCategoryPresent && isLoggedIn) {
            try {
                const { tags } = await getPlayerTags({ tagName: 'uam/hidden/playerRecentGames', isJSONParsed: true });

                if (tags?.[0]?.valueParsed) {
                    recentlyPlayedGames = tags[0].valueParsed as string[];
                }
            } catch (error) {
                log('error', '[Games Grid] Unable to get recently played games', error);
            }
        }

        // getting favorites games
        if (isLoggedIn) {
            try {
                await getFavoriteGames();
            } catch (error) {
                log('error', '[Games Grid] Unable to get favorite games', error);
            }
        }

        // getting pgg (personal game grid) games
        if (categoriesInfo.pggCategories.length > 0) {
            const promises: ReturnType<typeof getPggGames>[] = [];

            categoriesInfo.pggCategories.forEach(({ pggCategoryName, categoryName, maxGames }) => {
                promises.push(getPggGames({ pggCategoryName, categoryName, maxGames, allGames }));
            });

            try {
                pggCategoryResponses = await Promise.all(promises);
            } catch (error) {
                log('error', '[Games Grid] Unable to get PGG games', error);
            }
        }

        const gamesFeedCategories = filteredCategories.map((item) => {
            const category = clone(item) as Types.TGamesFeedCategoryParsed;
            const { categoryConfiguration } = category;
            const { categoryType } = categoryConfiguration;

            if (categoryType === CATEGORY_TYPES.recent) {
                categoryConfiguration.categoryGames = recentlyPlayedGames;
            }

            if (categoryType === CATEGORY_TYPES.favorites) {
                categoryConfiguration.categoryGames = [];
            }

            if (categoryType === CATEGORY_TYPES.pgg) {
                const pggCategory = pggCategoryResponses.find(({ categoryName }) => categoryName === category.name);

                if (pggCategory && pggCategory?.games.length >= Number(categoryConfiguration.pggMinGames)) {
                    categoryConfiguration.categoryGames = pggCategory.games;
                    categoryConfiguration.pggBecauseYouPlayedGameName = pggCategory.pggBecauseYouPlayedGameName;
                }
            }

            categoryConfiguration.categoryGames = categoryConfiguration.categoryGames.filter((gameCode) => {
                const isAvailable = isGameAvailable({ allGames, gameCode, isInternalPlayer });

                if (isAvailable) {
                    const game = allGames[gameCode];

                    if (game?.liveLaunchAlias) {
                        liveGamesAliases.add(game.liveLaunchAlias);
                    }
                }

                return isAvailable;
            });

            // Settings for view more mode
            if (isViewMoreMode) {
                categoryConfiguration.layoutsNumber = '';
                categoryConfiguration.categoryView = CATEGORY_VIEWS.grid;
                categoryConfiguration.categoryGameGridLayout = categoryConfiguration.viewMoreGameGridLayout;
            }

            return category;
        });

        return mergeDeepWith(clone, {
            liveGamesAliases,
            gamesFeedCategories
        }, gamesFeed);
    }, [
        browserName, country, formFactor, getFavoriteGames, getPggGames,
        getPlayerTags, isLoggedIn, lang, os, urlTemplates, categoryNameURLParam, isInternalPlayer
    ]);
};
